<template>
  <b-modal
    id="modal-nova-devolucao"
    :title="form.tipo === 'TROCA' ? 'Nova Solicitação de Troca' : 'Nova Solicitação de Devolução'"
    size="lg"
    hide-footer
    no-close-on-backdrop
    @hidden="resetar"
  >
    <validation-observer ref="form" #default="{ invalid }">
      <!-- Tipo -->
      <b-row class="mb-1">
        <b-col>
          <label class="font-weight-bold">Tipo de Solicitação</label>
          <div class="d-flex" style="gap:10px">
            <b-button
              :variant="form.tipo === 'DEVOLUCAO' ? 'primary' : 'outline-secondary'"
              @click="form.tipo = 'DEVOLUCAO'"
            >
              <feather-icon icon="RotateCcwIcon" size="14" class="mr-25" />
              Devolução
            </b-button>
            <b-button
              :variant="form.tipo === 'TROCA' ? 'warning' : 'outline-secondary'"
              @click="form.tipo = 'TROCA'"
            >
              <feather-icon icon="RepeatIcon" size="14" class="mr-25" />
              Troca
            </b-button>
          </div>
        </b-col>
      </b-row>

      <hr class="my-1" />

      <!-- Dados da solicitação -->
      <b-row>
        <b-col md="6">
          <b-form-group label="Pedido de Origem (opcional)">
            <b-input-group size="sm">
              <b-form-input v-model="form.pedidoid_origem" type="number" placeholder="Nº do pedido" />
              <b-input-group-append>
                <b-button variant="outline-primary" size="sm" :disabled="!form.pedidoid_origem || loadingItens" @click="importarItensPedido">
                  <b-spinner v-if="loadingItens" small />
                  <feather-icon v-else icon="DownloadIcon" size="13" />
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <small class="text-muted">Clique em ↓ para importar os itens do pedido</small>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Nota Fiscal (opcional)">
            <b-form-input v-model="form.notafiscal" size="sm" placeholder="Número da NF" />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <validation-provider #default="{ errors }" name="Motivo" rules="required">
            <b-form-group label="Motivo da Solicitação *">
              <b-form-textarea
                v-model="form.motivo"
                rows="2"
                size="sm"
                placeholder="Descreva o motivo da devolução ou troca..."
                :state="errors.length ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
        </b-col>
      </b-row>

      <!-- Itens -->
      <div class="mb-1">
        <div class="d-flex align-items-center justify-content-between mb-50">
          <label class="font-weight-bold mb-0">Itens</label>
          <b-button size="sm" variant="outline-primary" @click="adicionarItem">
            <feather-icon icon="PlusIcon" size="12" class="mr-25" />Adicionar Item
          </b-button>
        </div>

        <div v-if="!form.itens.length" class="text-muted small text-center py-1">
          Nenhum item adicionado. Importe do pedido ou adicione manualmente.
        </div>

        <b-table
          v-else
          :items="form.itens"
          :fields="itemFields"
          small
          responsive
          class="mb-0"
        >
          <template #cell(referencia)="{ index }">
            <b-form-input v-model="form.itens[index].referencia" size="sm" placeholder="Ref." style="min-width:80px" />
          </template>
          <template #cell(descricao)="{ index }">
            <b-form-input v-model="form.itens[index].descricao" size="sm" placeholder="Descrição" style="min-width:120px" />
          </template>
          <template #cell(des_cor)="{ index }">
            <b-form-input v-model="form.itens[index].des_cor" size="sm" placeholder="Cor" style="min-width:70px" />
          </template>
          <template #cell(des_tamanho)="{ index }">
            <b-form-input v-model="form.itens[index].des_tamanho" size="sm" placeholder="Tam." style="min-width:60px" />
          </template>
          <template #cell(quantidade)="{ index }">
            <b-form-input v-model.number="form.itens[index].quantidade" type="number" min="1" size="sm" style="min-width:60px" />
          </template>
          <template #cell(motivo_item)="{ index }">
            <b-form-input v-model="form.itens[index].motivo_item" size="sm" placeholder="Motivo..." style="min-width:120px" />
          </template>
          <template #cell(remover)="{ index }">
            <b-button size="sm" variant="flat-danger" @click="removerItem(index)">
              <feather-icon icon="Trash2Icon" size="12" />
            </b-button>
          </template>
        </b-table>
      </div>

      <b-row>
        <b-col>
          <b-form-group label="Observação adicional (opcional)">
            <b-form-textarea v-model="form.observacao" rows="2" size="sm" />
          </b-form-group>
        </b-col>
      </b-row>

      <div class="d-flex justify-content-end" style="gap:8px">
        <b-button variant="outline-secondary" @click="$bvModal.hide('modal-nova-devolucao')">Cancelar</b-button>
        <b-button variant="primary" :disabled="invalid || isSaving" @click="salvar">
          <b-spinner v-if="isSaving" small class="mr-25" />
          <feather-icon v-else icon="SendIcon" size="13" class="mr-25" />
          Enviar Solicitação
        </b-button>
      </div>
    </validation-observer>
  </b-modal>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { mapGetters, mapActions } from 'vuex';
import SLSDevolucaoClient from '@/api/Operacoes/sls-devolucao.client';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

const emptyItem = () => ({
  itemid: '', referencia: '', descricao: '', des_cor: '', cdn_cor: null,
  des_tamanho: '', cdn_embalagem: null, des_embalagem: '', quantidade: 1, motivo_item: '',
});

export default {
  name: 'NovaDevolucao',
  components: { ValidationObserver, ValidationProvider },
  data() {
    return {
      loadingItens: false,
      form: {
        tipo: 'DEVOLUCAO',
        pedidoid_origem: '',
        notafiscal: '',
        motivo: '',
        observacao: '',
        itens: [],
      },
      itemFields: [
        { key: 'referencia',  label: 'Ref.' },
        { key: 'descricao',   label: 'Descrição' },
        { key: 'des_cor',     label: 'Cor' },
        { key: 'des_tamanho', label: 'Tamanho' },
        { key: 'quantidade',  label: 'Qtd.' },
        { key: 'motivo_item', label: 'Motivo' },
        { key: 'remover',     label: '' },
      ],
    };
  },
  computed: {
    ...mapGetters('Devolucoes', ['isSaving']),
  },
  methods: {
    ...mapActions('Devolucoes', ['criar']),
    abrir() {
      this.$bvModal.show('modal-nova-devolucao');
    },
    resetar() {
      this.form = { tipo: 'DEVOLUCAO', pedidoid_origem: '', notafiscal: '', motivo: '', observacao: '', itens: [] };
      this.$refs.form?.reset?.();
    },
    adicionarItem() {
      this.form.itens.push(emptyItem());
    },
    removerItem(index) {
      this.form.itens.splice(index, 1);
    },
    async importarItensPedido() {
      if (!this.form.pedidoid_origem) return;
      this.loadingItens = true;
      try {
        const { data: res } = await new SLSDevolucaoClient().buscarPedidoItens(this.form.pedidoid_origem);
        if (res.data && res.data.length) {
          this.form.itens = res.data.map((i) => ({
            itemid: i.itemid || '',
            referencia: i.referencia || '',
            descricao: i.descricao || '',
            des_cor: i.des_cor || '',
            cdn_cor: i.cdn_cor || null,
            des_tamanho: i.des_tamanho || '',
            cdn_embalagem: i.cdn_embalagem || null,
            des_embalagem: i.des_embalagem || '',
            quantidade: parseFloat(i.quantidade) || 1,
            motivo_item: '',
          }));
          this.$toast({ component: ToastificationContent, props: { title: `${res.data.length} item(s) importado(s)`, icon: 'CheckIcon', variant: 'success' } });
        } else {
          this.$toast({ component: ToastificationContent, props: { title: 'Pedido sem itens ou não encontrado', icon: 'AlertTriangleIcon', variant: 'warning' } });
        }
      } catch {
        this.$toast({ component: ToastificationContent, props: { title: 'Erro ao buscar itens do pedido', icon: 'XIcon', variant: 'danger' } });
      } finally {
        this.loadingItens = false;
      }
    },
    async salvar() {
      const valid = await this.$refs.form.validate();
      if (!valid) return;

      try {
        const formData = this.$store.getters['InfoPedido/GET_FORM_INFO_PEDIDO'] || {};
        const clienteid = formData?.cliente?.clienteid || '';

        await this.criar({
          ...this.form,
          clienteid,
          pedidoid_origem: this.form.pedidoid_origem || null,
        });

        this.$toast({ component: ToastificationContent, props: { title: 'Solicitação enviada com sucesso!', icon: 'CheckIcon', variant: 'success' } });
        this.$bvModal.hide('modal-nova-devolucao');
        this.$emit('criado');
      } catch (err) {
        this.$toast({ component: ToastificationContent, props: { title: 'Erro ao enviar solicitação', text: err.message, icon: 'XIcon', variant: 'danger' } });
      }
    },
  },
};
</script>
